import React, { useMemo } from 'react';
import styles from './ClientsQueueModal.module.scss';
import AvatarIcon from '../../../Common/AvatarIcon';
import { ReactComponent as WhatsappIcon } from '../../../../assets/svg/icons/whatsapp.svg';
import { ReactComponent as PhoneIcon } from '../../../../assets/svg/icons/chiamate.svg';
import clx from 'classnames';
import IconButton from '../../../Common/Buttons/IconButton';
import useWindowSize from '../../../../utils/useWindowSize';
import { modalActions } from '../../../Modals/store';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Clock3, Users } from 'lucide-react';
import { getCalendarQueue } from '../../store/selectors';
import RemoveFromQueueButton from '../RemoveFromQueueButton';

const selectCalendarQueue = getCalendarQueue();

export default ({ calendarQueue }) => {
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  // Dati live dallo store (si aggiornano dopo una rimozione); le props indicano
  // quali poltrone mostrare (es. menu poltrona → una sola).
  const liveQueue = useSelector(selectCalendarQueue);
  const queueDate = calendarQueue?.date || liveQueue?.date || null;

  const workers = useMemo(() => {
    const requestedIds = (calendarQueue?.workers || []).map((worker) => String(worker?.id));
    const source = liveQueue?.workers?.length ? liveQueue.workers : calendarQueue?.workers || [];
    return source.filter(
      (worker) =>
        worker?.clients?.length > 0 &&
        (!requestedIds.length || requestedIds.includes(String(worker?.id))),
    );
  }, [calendarQueue, liveQueue]);

  const openClientModal = (id) => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'clientModal',
        props: { client_id: id },
      }),
    );
  };

  if (!workers.length) {
    return (
      <div className={clx(styles.queue_container, { [styles.queue_container_mobile]: isMobile })}>
        <div className={styles.empty_state}>
          <span className={styles.empty_icon}>
            <Users size={22} strokeWidth={2.2} />
          </span>
          <p className={styles.empty_text}>{t('scheduler.clientsQueueModal.empty')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clx(styles.queue_container, { [styles.queue_container_mobile]: isMobile })}>
      {workers.map((worker) => (
        <section key={worker.id} className={styles.worker_section}>
          <header className={styles.worker_header}>
            <AvatarIcon icon={worker.icon} name={worker.name} size="36px" />
            <div className={styles.worker_copy}>
              <h3 className={styles.worker_name}>{worker.name}</h3>
              <span className={styles.worker_meta}>
                {t('scheduler.clientsQueueModal.clientsCount', {
                  count: worker.clients.length,
                })}
              </span>
            </div>
          </header>

          <ul className={styles.clients_list}>
            {worker.clients.map((client) => (
              <li key={`${worker.id}-${client.queueId || client.id}`} className={styles.client_row}>
                <div className={styles.client_top}>
                  <button
                    type="button"
                    className={styles.client_main}
                    onClick={() => openClientModal(client.id)}
                  >
                    <AvatarIcon icon={client.icon} name={client.name} size="40px" />
                    <div className={styles.client_copy}>
                      <span className={styles.client_name}>{client.name}</span>
                      {client.duration ? (
                        <span className={styles.duration_pill}>
                          <Clock3 size={12} strokeWidth={2.4} aria-hidden="true" />
                          {t('scheduler.clientsQueueModal.duration', {
                            minutes: client.duration,
                          })}
                        </span>
                      ) : null}
                    </div>
                    {isMobile ? (
                      <ChevronRight
                        className={styles.client_chevron}
                        size={18}
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>

                  {!isMobile ? (
                    <div className={styles.client_actions}>
                      <IconButton
                        svg={<WhatsappIcon />}
                        type="whatsapp"
                        size="38px"
                        isActive={!!client.phone}
                        value={client.phone}
                        margin="0 6px 0 0"
                      />
                      <IconButton
                        svg={<PhoneIcon />}
                        type="phone"
                        size="38px"
                        isActive={!!client.phone}
                        value={client.phone}
                        margin="0 6px 0 0"
                      />
                      <button
                        type="button"
                        className={styles.details_btn}
                        onClick={() => openClientModal(client.id)}
                      >
                        {t('clients.table.details')}
                        <ChevronRight size={15} strokeWidth={2.2} aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className={styles.client_footer}>
                  <RemoveFromQueueButton
                    variant="text"
                    compact={isMobile}
                    label={t('scheduler.queue.remove.action')}
                    className={styles.remove_btn}
                    queueId={client.queueId}
                    clientName={client.name}
                    workerName={worker.name}
                    date={queueDate}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};
